"use client"

import { useRef } from "react";
import PropTypes from 'prop-types';
import ResourcesSection from "../../components/ResourcesSection/ResourcesSection";
import LevelUpAndTrustSection from "../../components/LevelUpAndTrustSection/LevelUpAndTrustSection";
import SecurinTable from "../../components/SecurinTable/SecurinTable";
import { motion, useInView } from 'framer-motion';
import Button from "../../components/global-component/DatasheetButtons";

const vulnerabilityIntelligence = {
  title: "Securin Vulnerability Intelligence Features, Widgets, & Capabilities",
  content: [
    {
      heading: "RCE/PE Vulnerabilities",
      description:
        "Categorize vulnerabilities by exploit type Remote Code Execution (RCE), Privilege Escalation (PE), Web App, Denial of Service (DoS) etc.",
    },
    {
      heading: "Trending",
      description:
        "Exclusive filters for vulnerabilities that are being actively searched in the deep and dark web and hacker networks in real time.",
    },
    {
      heading: "Ransomware and Threat Groups",
      description:
        "A living list of vulnerabilities with conclusive associations to ransomware operators and advanced persistent threat groups.",
    },
    {
      heading: "Exploits",
      description:
        "Exploits and their classifications and aliases with a timeline of when they became publicly available.",
    },
    {
      heading: "Temporal Scores",
      description:
        "Temporal scores for each vulnerability to accurately measure its risk based on the exploitability, report confidence, and remediation level.",
    },
    {
      heading: "CVSS V2 to V3 Mapping",
      description:
        "Mapping old vulnerabilities (discovered before 2015) with V2 scoring to CVSS V3 severity rating to provide uniformity in understanding the risk level of a vulnerability.",
    },
    {
      heading: "Advisories and Warnings",
      description:
        "View the latest advisories from CISA, FBI, and other security agencies about dangerous, trending vulnerabilities. Real time intelligence feeds an exclusive filtered view of vulnerabilities that are under active exploitation.",
    },
    {
      heading: "Zero Days",
      description:
        "An exclusive dashboard that lists newly discovered zero days (and also those without CVE numbers) with all pertinent details, severity scores, and predictive insights.",
    },
    {
      heading: "Predictive Intel",
      description:
        "Predictive scores for vulnerabilities based on their exploitability, evidence from hacker chats, deep and dark web forums, security community discussion in social media and more.",
    },
    {
      heading: "Affected Vendors and Products",
      description:
        "Common Platform Enumeration (CPE) data of vendors and products affected by vulnerabilities. Know the code base used in each product through the Software Bill of Materials (SBOM) and pivot fast to stay safe from supply chain attacks.",
    },
    {
      heading: "Vulnerability Data by Sectors, Product Categorization, EOL",
      description:
        "View vulnerabilities that exist in specific product categories such as VPNs, databases, data storage, gateways, online conference tools, web proxies, etc. Find vulnerabilities that pose threats to certain industries or sectors (health care, SLED, energy, critical infrastructure, etc.).",
    },
    {
      heading: "End of Life (EOL)",
      description:
        "Filter products that have reached the End-of-Life (EOL) life cycle to manage and stay on top of your asset management.",
    },
    {
      heading: "CISA KEVs",
      description:
        "Threat context for CISA's Known Exploited Vulnerabilities (KEVs) catalog, real-time alerts, and updates.",
    },
  ],
};

const VulnerabilityIntelligence = ({ blok }) => {

  const levelUpRef = useRef(null);


  const handleScrollToLevelUp = () => {
    levelUpRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);
  const ref5 = useRef(null);
  const ref6 = useRef(null);
  const ref7 = useRef(null);
  const ref8 = useRef(null);

  const isInView1 = useInView(ref1, { once: true, margin: "-100px" });
  const isInView2 = useInView(ref2, { once: true, margin: "-100px" });
  const isInView3 = useInView(ref3, { once: true, margin: "-100px" });
  const isInView4 = useInView(ref4, { once: true, margin: "-100px" });
  const isInView5 = useInView(ref5, { once: true, margin: "-100px" });
  const isInView6 = useInView(ref6, { once: true, margin: "-100px" });
  const isInView7 = useInView(ref7, { once: true, margin: "-100px" });
  const isInView8 = useInView(ref8, { once: true, margin: "-100px" });

  const handleDashboardClick = () => {
    window.open(blok?.DatasheetCta?.[0]?.asset?.filename, "_blank");
  };

  console.log({blok})
  return (
    <>
      <div
        className="bg-asmPageTopBG w-full"
        style={{
          backgroundSize: "cover", // Ensure the image covers the entire section
          backgroundPosition: "center", // Center the background image
          backgroundRepeat: "repeat", // Prevent repeating the background
          // maxWidth: 1920,
          margin: "auto",
        }}
      >
        {/* <AppBar /> */}
        <div className="relative min-h-[1000px] flex flex-col items-center py-8  m-auto  lg:px-[24px] lg:px-0">
          {/* Header Section */}
          <div className="flex flex-col gap[4px] font-aileron pt-32 text-center font-bold">
            <h1 className="bg-custom-radial bg-clip-text text-transparent text-[48px] lg:text-[80px] leading-[112%] md:leading-[125%]">
              {blok.titleText1}
            </h1>
            <div className="text-[18px] m-auto lg:w-full lg:text-[32px] font-normal mt-[16px] md:mt-auto px-4 xl:px-0">
              {blok.subtitleText1}
            </div>
            <div className="mt-[30px] flex justify-center gap-6">
              <button onClick={handleScrollToLevelUp}
                rel="noopener noreferrer" className="font-aileron flex items-center justify-center text-[18px] lg:text-[24px] font-normal bg-[#5A27E7] !text-white px-6 py-2 rounded-[4px] hover:bg-[#483889] lg:w-[255px] lg:h-[64px]">
                {blok.buttonText1}
              </button>
            </div>
          </div>

          {/* Main Content Section */}
          <div ref={ref1} className="flex flex-col lg:flex-row flex-wrap items-center lg:justify-center gap-[72px] max-w-7xl z-20 justify-center mt-[50px] mx-4">
            {/* Left Column (Main Image) */}
            <div className="w-full lg:w-auto">
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                animate={isInView1 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="flex justify-center items-center m-auto lg:m-0"
              >
                <img 
                  src={blok.image2.filename} 
                  className="w-[90%] sm:w-[80%] md:w-full lg:w-auto max-w-[600px]"
                />
              </motion.div>
            </div>


            {/* Right Column (Full Image of Diagram) */}
            <div className="flex-1 flex flex-col justify-end">
              {/* Description */}
              <h2 className="flex justify-center lg:flex lg:justify-start font-aileron text-[28px] lg:text-[48px] !font-bold bg-custom-radial bg-clip-text text-transparent mb-7 text-center lg:text-start leading-normal md:leading-[55px]">
                {blok.titleText2}
              </h2>
              <p className="md:mt-[28px] font-aileron text-[16px] lg:text-[18px] lg:mb-6 m-auto mb-2 lg:m-0 text-center lg:text-start">
                {blok.descriptionText2Para1}
              </p>
              <p className="font-aileron text-[16px] lg:text-[18px] lg:mb-[50px] m-auto mb-6 lg:m-0 text-center lg:text-start">
                {blok.descriptionText2Para2}
              </p>
             <div className="flex justify-center items-center lg:justify-start">
             <Button title={blok?.DatasheetCta?.[0]?.text} onClick={handleDashboardClick} />
             </div>
            </div>
          </div>
          {/* <div className='absolute top-[53%]'>
            <img src={asmCurve} />
          </div> */}
        </div>
      </div>

      <div className="relative min-h-[1000px] mt-0  m-auto z-0 w-full">
        <div>
          <h6 className="px-[24px] lg:px-0 flex justify-center lg:block font-aileron bg-custom-radial bg-clip-text text-transparent text-[28px] lg:text-[48px] font-bold mt-4 lg:mt-[90px] mb-4 lg:mb-8 text-center leading-normal md:leading-[55px] ">
            {blok.titleText3}
          </h6>
          <div className="px-[24px] lg:px-0 font-aileron flex justify-center items-center lg:max-w-7xl text-center text-[16px] lg:text-[18px] m-auto">
            {blok.descriptionText3Para1}<br /> <br />{blok.descriptionText3Para2}
            <br /> <br />
            {blok.descriptionText3Para3}
          </div>
          <div ref={ref2}
            className="flex justify-center items-center mt-6 w-full"
          // style={{
          //   backgroundSize: "cover", // Ensure the image covers the entire section
          //   backgroundPosition: "center", // Center the background image
          //   backgroundRepeat: "repeat", // Prevent repeating the background
          //   maxWidth: 1920,
          //   margin: "auto",
          // }}
          >
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={isInView2 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-[30px] mb-[80px] px-[24px] lg:px-0 max-w-7xl"
            >
              <img
                src={blok.image3.filename}
                className="object-center"
              />
            </motion.div>
          </div>
          <div ref={ref3} className="lg:w-full lg:flex justify-center bg-footer-bg ">
            <div className="max-w-7xl lg:flex justify-center gap-20  py-[24px] px-[24px]">
              <motion.img
                initial={{ opacity: 0, x: -100 }}
                animate={isInView3 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                src={blok.image4.filename}
                className="lg:w-[50%]  w-full object-cover pt-10 lg:pt-0 px-[24px] lg:px-0"
              />
              <div className="lg:w-[50%] flex flex-col justify-center">
                <h2 className="font-aileron text-[28px] lg:text-[48px] font-semibold !text-white mt-10 mb-6 text-center lg:text-start m-auto lg:mt-10 lg:m-0 lg:mb-6 leading-normal md:leading-[57px]">
                  {blok.titleText4}
                </h2>
                <p className="font-lato text-[16px] lg:text-[18px] lg:mb-6   m-auto mb-2 lg:m-0 text-center lg:text-start !text-white">
                  {blok.descriptionText4Para1} <br /> <br/>
                  {blok.descriptionText4Para2}
                </p>
              </div>
            </div>
          </div>
          <SecurinTable data={vulnerabilityIntelligence} blok={blok} />
        </div>
        <div
          className="m-auto -mb-10 min-h-[1700px] bg-asmPageBottomBG flex flex-col items-center"
          style={{
            backgroundSize: "cover", // Ensure the image covers the entire section
            backgroundPosition: "center", // Center the background image
            backgroundRepeat: "no-repeat", // Prevent repeating the background
            // maxWidth: 1920,
            margin: "auto",
          }}
        >
          <div className="lg:flex justify-center items-center text-center  px-[24px] lg:px-0">
            <div ref={ref4} className="flex flex-col lg:flex-row flex-wrap gap-16 justify-center  items-center text-start py-14 md:py-28 max-w-7xl px-0 xl:px-0">
              <motion.img initial={{ opacity: 0, x: -100 }}
                animate={isInView4 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                src={blok.image6.filename}
                className="flex-1 mx-auto lg:mx-0 lg:w-[50%] object-cover"
              />
              <div className="flex-1 text-center lg:text-start m-auto lg:m-0 mt-10 lg:mt-0">
                <h1 className="font-aileron bg-custom-radial bg-clip-text text-transparent text-[24px] lg:text-[48px] font-bold leading-normal md:leading-[55px]">
                  {blok.titleText6}
                </h1>
                <p className="font-lato text-[16px] lg:text-[18px] mt-2 lg:mt-4">
                  {blok.descriptionText6Para1}
                  <br /> <br/>
                  {blok.descriptionText6Para2}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center text-center justify-center mb-10 w-full">
            <h1 className="font-aileron bg-custom-radial bg-clip-text text-transparent text-[24px] lg:text-[48px] font-bold mb-4 lg:mb-7 px-[24px] lg:px-0">
              {blok.titleText7}
            </h1>
            <p className="font-lato text-[16px] lg:text-[18px] md:max-w-7xl px-[24px] lg:px-0">
              {blok.descriptionText7Para1}
            </p>
            <div ref={ref5} className="flex flex-col items-center lg:flex-row xl:w-[1496px] bg-white rounded-[40px] lg:flex mx-2 py-10 gap-10 lg:gap-20 lg:mx-0 px-6 my-16">
              <div className="xl:w-[40%] text-center lg:text-start w-[100%] m-auto lg:m-0 xl:ml-40">
                <h1 className="font-aileron bg-custom-radial bg-clip-text text-transparent text-[24px] lg:text-[48px] font-bold">
                  {blok.titleText8}
                </h1>
                <p className="font-lato text-[16px] lg:text-[18px] mt-2 lg:mt-4">
                  {blok.descriptionText8Para1}
                  <br /> <br />
                  {blok.descriptionText8Para2}
                </p>
              </div>
              <motion.img initial={{ opacity: 0, x: 100 }}
                animate={isInView5 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                src={blok.image8.filename}
                className="lg:h-[388px] lg:w-[473px] w-[400px]"
              />
            </div>
            <p className="font-lato text-[16px] lg:text-[18px] md:max-w-7xl mb-20 px-[24px] xl:px-0">
              {blok.descriptionText7Para2}
            </p>
            <div ref={ref6} className="mt-10 mb-10 rounded-lg  px-[24px] lg:px-0 flex justify-center">
              {" "}
              <motion.img initial={{ opacity: 0, y: -50 }}
                animate={isInView6 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }} src={blok.image7.filename} 
                className="lg:max-w-7xl"
                />{" "}
            </div>
          
            <div className="w-full lg: lg:h-[367px] bg-footer-bg mt-20  px-[24px] lg:px-0 py-[24px] lg:py-0">
              <h1 className="font-aileron !text-white text-[20px] lg:text-[42px] font-bold lg:mt-10">
                {blok.titleText9}
              </h1>
              <p className="font-lato !text-white text-[14px] lg:text-[18px] m-auto mt-4 max-w-7xl md:px-[70px]">
                {blok.descriptionText9}
              </p>
            </div>
            <div ref={ref7} className="border-4 border-[#cecadd] rounded-[8px] my-[10px] mx-[16px] lg:my-0 lg:mx-0 lg:max-w-7xl m-auto lg:mt-10 lg:-translate-y-[120px]">
              <motion.img initial={{ opacity: 0, y: 100 }}
                animate={isInView7 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                src={blok.image9.filename}
                className="w-full"
              />
            </div>
            <div className="mb-10 mt-20 lg:mt-[-40px]">
              <div className="flex justify-center mb-8 md:mb-10  px-[24px] lg:px-0">
                <h1 className="font-aileron bg-custom-radial bg-clip-text text-transparent text-[24px] lg:text-[48px] font-bold">
                  {blok.titleText10}
                </h1>
              </div>
              <div ref={ref8} className="flex flex-wrap max-w-7xl gap-10 md:gap-[50px] m-auto lg:w-full  px-[24px] lg:px-0">
                <motion.div
                  initial={{ opacity: 0, x: -100 }}
                  animate={isInView8 ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative flex-1 p-[4px] rounded-lg bg-gradient-to-b from-[#D259A6]/80 to-[#48398C]/50">
                  {/* Inner content div */}
                  <div className=" h-full px-[24px] w-full bg-ASMBenefitsBg rounded-lg flex flex-col justify-center items-center text-center py-[32px]" >
                    <img src={blok.cardImage1.filename} className="h-[156px] w-[155px] object-contain mb-6" />
                    <p className="font-lato text-[24px] text-[#49378F] font-bold -mt-4 mb-2 sm:w-max">
                      {blok.cardTitle1}
                    </p>
                    <p className="text-[18px] font-lato">
                      {blok.cardDescription1}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={isInView8 ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="relative flex-1 rounded-lg p-[4px] bg-gradient-to-b from-[#D259A6]/80 to-[#48398C]/50">
                  {/* Inner content div */}
                  <div className="h-full w-full bg-ASMBenefitsBg rounded-lg flex flex-col justify-center items-center text-center px-[24px] py-[32px]">
                    <img src={blok.cardImage2.filename} className="h-[128px] w-[128px] object-contain mb-6" />
                    <p className="font-lato text-[24px] text-[#49378F] font-bold -mt-4 mb-2 sm:w-max">
                      {blok.cardTitle2}
                    </p>
                    <p className="text-[18px] font-lato">
                      {blok.cardDescription2}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  animate={isInView8 ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="relative flex-1 rounded-lg p-[4px] bg-gradient-to-b from-[#D259A6]/80 to-[#48398C]/50">
                  {/* Inner content div */}
                  <div className="h-full px-[24px] w-full bg-ASMBenefitsBg rounded-lg flex flex-col justify-center items-center text-center py-[32px]">
                    <img src={blok.cardImage3.filename} className="h-[128px] w-[128px] object-contain mb-6" />
                    <p className="font-lato text-[24px] text-[#49378F] font-bold -mt-4 mb-2 sm:w-max">
                      {blok.cardTitle3}
                    </p>
                    <p className="text-[18px] font-lato">
                      {blok.cardDescription3}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#EFEBFB]">
          <div className="space-y-6">
          <ResourcesSection
             calenderIcon={blok.calenderIcon.filename}
              blok={blok}
            />
            <LevelUpAndTrustSection certiReqd={true} blok={blok} levelUpRef={levelUpRef} />

          </div>
          {/* <Footer blok={blok} /> */}
        </div>
      </div>
    </>
  );
};
VulnerabilityIntelligence.propTypes = {
  blok: PropTypes.shape({
    buttonText1: PropTypes.string.isRequired,
    // add other blok properties here
  }).isRequired
};

export default VulnerabilityIntelligence;
